import { useEffect, useId, useRef, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { ArrowBackIcon } from "../icons.tsx";
import styles from "./BottomSheet.module.css";

type BottomSheetProps = {
  open: boolean;
  /** Закрытие по Escape, тапу по фону или изнутри шторки. */
  onClose: () => void;
  title: string;
  /** Шапка со стрелкой «Назад» слева от заголовка вместо Drag Handle (Header Row, Figma 829:3629). */
  onBack?: () => void;
  /** Действие справа в шапке со стрелкой (нужен `onBack`), например «Черновики» в compose (08.02, Figma 1023:4691). */
  action?: ReactNode;
  /**
   * Шторка на всю допустимую высоту, содержимое — колонкой без собственной прокрутки шторки:
   * длинный список прокручивается в своём контейнере, а шапка остаётся на месте.
   */
  fill?: boolean;
  children: ReactNode;
};

/** Шторка (Sheet из Figma, например 06.01 — узел 682:6431) на модальном `<dialog>`. */
export function BottomSheet({ open, onClose, title, onBack, action, fill = false, children }: BottomSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={fill ? `${styles.sheet} ${styles.fill}` : styles.sheet}
      aria-labelledby={titleId}
      // Только своё закрытие: `close` вложенной шторки (например, эмодзи в compose) сюда не относится.
      onClose={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      // Клик мимо содержимого попадает в сам <dialog>: это тап по затемнённому фону.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {open && (
        <div className={styles.body}>
          {!onBack && <span className={styles.handle} aria-hidden="true" />}
          <div className={headerClassName(onBack !== undefined, Boolean(action))}>
            {onBack && (
              <button type="button" className={styles.back} aria-label="Назад" onClick={onBack}>
                <ArrowBackIcon />
              </button>
            )}
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {onBack && action && <div className={styles.action}>{action}</div>}
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

/**
 * Закрытие шторки-маршрута: открыли из приложения — возвращаемся туда же, как системная «назад»;
 * открыли по прямой ссылке (истории позади нет) — уходим на `fallback`.
 */
export function useCloseSheet(fallback: string): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    if (location.key === "default") void navigate(fallback, { replace: true });
    else void navigate(-1);
  };
}

function headerClassName(withBack: boolean, withAction: boolean): string | undefined {
  if (!withBack) return undefined;
  return withAction ? `${styles.header} ${styles.withAction}` : styles.header;
}
